/**
 * Задержка команды: кольцо замеров и статистика.
 *
 * Критерий приёмки вехи M5 — «измерена и стабильна задержка клик → POST в
 * CLOB». Полностью этот путь виден только отсюда: трейдер меряет его с момента
 * чтения команды из стрима, а до того были браузер, web_api, аудит в Postgres и
 * XADD.
 *
 * # Три часов и что с чем можно вычитать
 *
 * Правило вехи M3 гласит: `received_at` — часы трейдера, «нет обновлений N с» —
 * часы браузера, и вычитать одно из другого нельзя. Здесь оно нужно в
 * уточнённом виде, и уточнение принципиально:
 *
 *   Вычитать ОТМЕТКИ ВРЕМЕНИ с разных часов нельзя — их смещение произвольно.
 *   Вычитать ДЛИТЕЛЬНОСТИ, измеренные каждая своими часами, можно: смещение
 *   сокращается, остаётся расхождение ХОДА, а это единицы ppm — на интервале в
 *   триста миллисекунд наносекунды.
 *
 * Отсюда: `command.accepted_at` (часы web_api) в арифметике не участвует
 * НИКОГДА, он только для показа. А «остальное» = (клик → вердикт) −
 * latency_ms трейдера — законно, потому что это разность двух длительностей.
 *
 * Всё браузерное меряется performance.now(), а не Date.now(): последний умеет
 * прыгнуть от коррекции NTP прямо посреди замера.
 */

import type { PendingKind } from "../store/orders";

/** Откуда пришла команда — чтобы веха могла ответить, помогли ли хоткеи. */
export type CommandSource = "form" | "quick" | "hotkey";

export interface Sample {
  source: CommandSource;
  kind: PendingKind;
  /** мс, ЧАСЫ БРАУЗЕРА (монотонные): клик → 202 от web_api. */
  toAcceptedMs: number | null;
  /** мс, ЧАСЫ БРАУЗЕРА (монотонные): клик → вердикт в сокете. */
  roundTripMs: number;
  /** мс, ЧАСЫ ТРЕЙДЕРА. Как прислано в command_result.latency_ms, без пересчёта. */
  traderMs: number;
  accepted: boolean;
  /**
   * Когда замер снят, `performance.now()`. Те же монотонные часы браузера, что и
   * у самих длительностей, — сравнивать их с `Date.now()` нельзя.
   *
   * Нужно, чтобы «сейчас» можно было отличить от «час назад»: кольцо живёт всю
   * жизнь вкладки, и без отметки времени один плохой эпизод оставался бы
   * медианой навсегда (см. `recent`).
   */
  at: number;
}

/**
 * Двухсот хватает примерно на час активного скальпа. Вкладка живёт сутками, и
 * неограниченный массив пережил бы её ровно один раз.
 */
export const CAPACITY = 200;

export function push(series: readonly Sample[], x: Sample): Sample[] {
  const next = [...series, x];
  return next.length > CAPACITY ? next.slice(next.length - CAPACITY) : next;
}

/**
 * Квантиль методом БЛИЖАЙШЕГО РАНГА.
 *
 * Не линейная интерполяция, и это осознанно: при n ≤ 200 и тяжёлом хвосте
 * интерполированный p95 выдумывает значение, которого не было ни разу, а это
 * число идёт в критерий приёмки. Ближайший ранг всегда возвращает замер,
 * который действительно случился.
 */
export function quantile(sorted: readonly number[], q: number): number | null {
  if (sorted.length === 0) return null;
  const rank = Math.ceil(q * sorted.length) - 1;
  const index = Math.min(Math.max(rank, 0), sorted.length - 1);
  return sorted[index] ?? null;
}

export interface Stats {
  n: number;
  p50: number | null;
  p95: number | null;
  min: number | null;
  max: number | null;
}

const EMPTY: Stats = { n: 0, p50: null, p95: null, min: null, max: null };

export function summarize(values: readonly number[]): Stats {
  if (values.length === 0) return EMPTY;
  const sorted = [...values].sort((a, b) => a - b);
  return {
    n: sorted.length,
    p50: quantile(sorted, 0.5),
    p95: quantile(sorted, 0.95),
    min: sorted[0] ?? null,
    max: sorted[sorted.length - 1] ?? null,
  };
}

export interface Breakdown {
  /** Клик → 202 от web_api. Часы браузера. */
  toAccepted: Stats;
  /** Клик → вердикт в сокете. Часы браузера. ВЕРХНЯЯ граница «клик → POST». */
  roundTrip: Stats;
  /** Стрим → ответ CLOB. Часы ТРЕЙДЕРА, как прислано. */
  trader: Stats;
  /** Разность ДЛИТЕЛЬНОСТЕЙ: всё, кроме обращения к бирже. */
  overhead: Stats;
  /** Сколько замеров выброшено из overhead из-за отрицательной разности. */
  negativeOverhead: number;
  /** Отклонённые команды: считаются отдельно и в заголовочные числа не входят. */
  rejected: number;
}

/**
 * Сводка по выборке.
 *
 * Два умолчания, каждое из которых иначе льстило бы числам:
 *
 *   - ОТКЛОНЁННЫЕ команды исключаются. Отказ риск-гейта — самый быстрый
 *     возможный круг (до биржи он не доходит вовсе), и его traderMs около нуля
 *     занизил бы и p50, и оценку «остального»;
 *   - ОТРИЦАТЕЛЬНАЯ разность (трейдер намерил больше, чем браузер) — это факт о
 *     развёртывании, а не о задержке. Такой замер выбрасывается из «остального»
 *     (но остаётся в остальных строках) и показывается счётчиком.
 *
 * Команды, не получившие вердикта вовсе, сюда не попадают по построению — у них
 * нет roundTripMs. Их считает панель по pending.timedOut и показывает рядом:
 * это худшие случаи, и молча терять их значит льстить p95.
 */
export function breakdown(series: readonly Sample[], source?: CommandSource): Breakdown {
  const scope = source ? series.filter((s) => s.source === source) : series;
  const accepted = scope.filter((s) => s.accepted);

  const overhead: number[] = [];
  let negative = 0;
  for (const s of accepted) {
    const rest = s.roundTripMs - s.traderMs;
    if (rest < 0) {
      negative++;
      continue;
    }
    overhead.push(rest);
  }

  return {
    toAccepted: summarize(
      accepted.map((s) => s.toAcceptedMs).filter((v): v is number => v !== null),
    ),
    roundTrip: summarize(accepted.map((s) => s.roundTripMs)),
    trader: summarize(accepted.map((s) => s.traderMs)),
    overhead: summarize(overhead),
    negativeOverhead: negative,
    rejected: scope.length - accepted.length,
  };
}

/** «214 / 480» либо «—», если мерить нечего. */
export function formatStats(s: Stats): string {
  if (s.p50 === null || s.p95 === null) return "—";
  return `${Math.round(s.p50)} / ${Math.round(s.p95)}`;
}

/**
 * Замеры за последние `windowMs`.
 *
 * # Зачем окно, если есть кольцо
 *
 * Затем, что кольцо не забывает. Двухсот замеров хватает примерно на час
 * активного скальпа, но вкладка живёт сутками, а команд у скальпера бывает
 * единицы за сессию — и тогда двести замеров это вся неделя. Разбор 13.08.2026:
 * оператор поймал эпизод в 21 и 50 секунд (не наш — сервер в тот день отвечал за
 * 41–50 мс), и p50 остался бы этим числом до перезагрузки страницы, обвиняя
 * исправную систему.
 *
 * Поэтому ГОЛОВНОЕ число считается по окну, а полный разбор в панели — по всему
 * кольцу: там это артефакт приёмки M5, и обрезать его нельзя.
 *
 * Граница ВКЛЮЧАЮЩАЯ: замер ровно на краю окна — это замер внутри него, и
 * выбрасывать его значило бы терять последний при точном совпадении часов.
 */
export function recent(series: readonly Sample[], windowMs: number, now: number): Sample[] {
  const from = now - windowMs;
  return series.filter((s) => s.at >= from);
}

/** Последний замер или null. Кольцо хранит их в порядке прихода. */
export function last(series: readonly Sample[]): Sample | null {
  return series.length === 0 ? null : (series[series.length - 1] ?? null);
}
